import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';

interface EditSettingModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: EditSettingData) => void;
  saving?: boolean;
  saveError?: string | null;
}

export interface EditSettingData {
  id: string;
  settingKey: string;
  settingValue: string;
}

const EditSettingModal: React.FC<EditSettingModalProps> = ({
  open,
  onClose,
  onSave,
  saving = false,
  saveError = null,
}) => {
  const { selectedSetting, loading } = useAppSelector((state) => state.globalSettings);

  const [settingValue, setSettingValue] = useState('');

  useEffect(() => {
    if (selectedSetting) {
      setSettingValue(selectedSetting.settingValue);
    }
  }, [selectedSetting]);

  const handleSave = () => {
    if (selectedSetting) {
      onSave({
        id: selectedSetting.id,
        settingKey: selectedSetting.settingKey,
        settingValue,
      });
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!selectedSetting && !loading) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Editar Configuración Global</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : selectedSetting ? (
          <Box sx={{ mt: 2 }}>
            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}
            <TextField
              fullWidth
              label="ID"
              value={selectedSetting.id}
              margin="normal"
              InputProps={{ readOnly: true }}
              variant="filled"
              disabled
            />
            <TextField
              fullWidth
              label="Clave (Key)"
              value={selectedSetting.settingKey}
              margin="normal"
              InputProps={{ readOnly: true }}
              variant="filled"
              disabled
            />
            <TextField
              fullWidth
              label="Valor (Value)"
              value={settingValue}
              onChange={(e) => setSettingValue(e.target.value)}
              margin="normal"
              required
              disabled={saving}
            />
            <TextField
              fullWidth
              label="Tipo"
              value={selectedSetting.settingType}
              margin="normal"
              InputProps={{ readOnly: true }}
              variant="filled"
              disabled
            />
            <TextField
              fullWidth
              label="Descripción"
              value={selectedSetting.description}
              margin="normal"
              multiline
              rows={3}
              InputProps={{ readOnly: true }}
              variant="filled"
              disabled
            />
            <TextField
              fullWidth
              label="Creado el"
              value={selectedSetting.createdAt}
              margin="normal"
              InputProps={{ readOnly: true }}
              variant="filled"
              disabled
            />
            <TextField
              fullWidth
              label="Creado por"
              value={selectedSetting.createdBy}
              margin="normal"
              InputProps={{ readOnly: true }}
              variant="filled"
              disabled
            />
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving || !settingValue}
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSettingModal;
